const uniq = values => [...new Set(values)];
// list of all the supported events in the ReactJWPlayer component
// https://github.com/micnews/react-jw-player/blob/master/src/default-props.js
export const JW_EVENT_NAMES = [
  'onAdPlay',
  'onAdResume',
  'onAdSkipped',
  'onAdComplete',
  'onEnterFullScreen',
  'onExitFullScreen',
  'onMute',
  'onUnmute',
  'onAutoStart',
  'onResume',
  'onPlay',
  'onClose',
  'onReady',
  'onError',
  'onAdPause',
  'onPause',
  'onVideoLoad',
  'onOneHundredPercent',
  'onThreeSeconds',
  'onTenSeconds',
  'onThirtySeconds',
  'onTwentyFivePercent',
  'onFiftyPercent',
  'onSeventyFivePercent',
  'onNinetyFivePercent',
  'onTime',
  'onBuffer',
  'onBufferChange',

  // the following are not defined in the default props; they're dynamic. see
  // https://github.com/micnews/react-jw-player/pull/93
  //
  // CLEANUP: refactor this whole thing to allow for entirely dynamic event
  // props. we still have to worry about overwriting onReady, though so it's
  // kinda tricky.
  'onSeek',
  'onSeeked',
  'onBeforePlay',
  'onComplete',
  'onAdError',
  'onViewable'
];

/**
 * returns an event handler that that will call the passed customHandler with
 * the event and the player object in the bound context.
 *
 * NOTE: the returned method is (later) bound to the instance so it has access
 * to the 'player' prop, which is important.
 *
 * @export
 * @param {Function} customHandler a custom event handler for the event
 * @returns {Function} event handler
 */
export function createDefaultEventHandler(customHandler) {
  // can't use arrow syntax here because we need to bind these to the instance.
  return function defaultEventHandler(e) {
    // nothing interesting here; just call the custom handler.
    customHandler(e, this.player, this.categoryContext);
  };
}

/**
 * iterates over all the event names and assigns matching keys of passed
 * customEventHandlers object as values, wrapped in a default event handler.
 * if no matching key in customEventHandlers is found, key is set to undefined.
 *
 * @export
 * @param {Object} [customEventHandlers={}] keys are values from JW_EVENT_NAMES;
 * values are functions that take the player event as an arg with the context of
 * the current instance
 * @returns {Object} keys are JW_EVENT_NAMES; values are event handler functions
 */
export function createEventHandlers(customEventHandlers = {}) {
  return JW_EVENT_NAMES.reduce(
    (acc, curr) =>
      Object.assign({}, acc, {
        // only create a wrapper method if a custom event handler is passed
        [curr]: customEventHandlers[curr]
          ? createDefaultEventHandler(customEventHandlers[curr])
          : undefined
      }),
    {}
  );
}

/**
 * accepts multiple objects whose values are functions (event handlers).
 * aggregates the objects' keys into a new object whose values are new functions
 * wrapper functions that and assigns its value to a wrapper function that calls
 * the original values.
 *
 * given:
 *
 *   const set1 = { play: set1play, pause: set1pause };
 *   const set2 = { play: set2play, stop: set2stop };
 *
 * then:
 *
 *   aggregateEvents(set1, set2)
 *
 * returns:
 *
 * {
 *   play: () => { set1play(); set2play(); },
 *   pause: () => { set1pause(); },
 *   stop: () => { set2stop(); }
 * }
 *
 * @export
 * @param {Object[]} eventSets array of sets of events
 * @param {Object} eventSets[] key is event name; value is event handler function
 * @return {Object} aggregate event set with values as wrapping function
 */
export function aggregateEvents(...eventSets) {
  // construct array of unique keys (event names) across all event sets
  const allEventNames = eventSets.reduce(
    (acc, curr) => uniq(acc.concat(Object.keys(curr))),
    []
  );

  // make an object with unique event names as keys and values as an empty
  // array. result: { a: [], b: [] }
  const eventMap = allEventNames.reduce(
    (acc, curr) =>
      Object.assign({}, acc, {
        [curr]: []
      }),
    {}
  );

  // now sort/group/push each sets' events into the correct eventMap key.
  // result: { a: [1a, 2a], b: [2b, 3b] };
  eventSets.forEach(set => {
    Object.keys(set).forEach(eventName => {
      eventMap[eventName].push(set[eventName]);
    });
  });

  // create the final structure: key as event name and value as a function that
  // wraps the individual functions from each set.
  //
  // note that this wrapping function accepts the same arguments and passes them
  // down to the individual functions as they are called when iterating over the
  // array of functions.
  return allEventNames.reduce(
    (acc, curr) =>
      Object.assign({}, acc, {
        [curr]: (...args) => [...eventMap[curr]].map(ev => ev(...args))
      }),
    {}
  );
}
